import { useState, useEffect } from 'react';
import axios from 'axios';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useNavigate, Link } from 'react-router-dom';

const SignUp = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    name: '',
    password: '',
    confirmPassword: '',
    role: 'STREAMER' // Default role
  });
  const [otp, setOtp] = useState('');
  const [secretId, setSecretId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errors, setErrors] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  // Check for dark mode preference
  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModeMediaQuery.matches);
    
    const handleChange = (e) => setIsDarkMode(e.matches);
    darkModeMediaQuery.addEventListener('change', handleChange);
    
    return () => darkModeMediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Also check for data-theme attribute on document
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme === 'dark');
    };
    
    checkTheme();
    
    // Create a MutationObserver to watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    
    return () => observer.disconnect();
  }, []);

  const showMessage = (text, type = 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number input - only allow digits
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      setFormData({ ...formData, [name]: digitsOnly });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error on typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Validation functions
  const validateEmail = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePhone = () => {
    const newErrors = {};
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.length < 10) {
      newErrors.phone = 'Phone number must be at least 10 digits';
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must contain only digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAccountInfo = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Start cooldown timer for resend OTP
  const startCooldown = () => {
    setResendCooldown(30);
    
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 1: Send Email OTP
  const handleSendEmailOtp = async (e) => {
    e.preventDefault();
    
    if (!validateEmail()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/sendEmailOtp`, {
        email: formData.email,
        secretId: secretId || undefined
      });

      setSecretId(response.data.data.secretId);
      setStep(2);
      showMessage(`OTP sent to ${formData.email}`, 'success');
      startCooldown();
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Resend Email OTP
  const handleResendEmailOtp = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/sendEmailOtp`, {
        email: formData.email,
        secretId: secretId || undefined
      });

      setSecretId(response.data.data.secretId);
      showMessage(`OTP resent to ${formData.email}`, 'success');
      startCooldown();
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify Email OTP
  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    
    if (!otp) {
      setErrors({ otp: 'OTP is required' });
      return;
    } else if (otp.length !== 4) {
      setErrors({ otp: 'OTP must be 4 digits' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/auth/verifyEmailOtp`, {
        secretId,
        otp
      });

      setOtp('');
      setStep(3);
      showMessage('Email verified successfully', 'success');
    } catch (error) {
      showMessage(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Account Creation
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    // Validate phone and account info
    if (!validatePhone() || !validateAccountInfo()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/auth/createAccount`, {
        secretId,
        name: formData.name,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        role: 'STREAMER', // Always send STREAMER as role
        phone: formData.phone
      });

      showMessage(`Account created successfully as STREAMER`, 'success');
      navigate('/signin');
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Theme-based classes
  const bgColor = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBgColor = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = isDarkMode ? 'text-gray-100' : 'text-gray-900';
  const labelColor = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const inputBgColor = isDarkMode ? 'bg-gray-700' : 'bg-white';
  const inputBorderColor = isDarkMode ? 'border-gray-600' : 'border-gray-300';
  const inputFocusRing = isDarkMode ? 'focus:ring-blue-500 focus:border-blue-500' : 'focus:ring-indigo-500 focus:border-indigo-500';
  const linkColor = isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-indigo-600 hover:text-indigo-500';
  const buttonBg = isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-indigo-600 hover:bg-indigo-700';
  const secondaryButtonBg = isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-white hover:bg-gray-50 text-gray-700';
  const secondaryButtonBorder = isDarkMode ? 'border-gray-600' : 'border-gray-300';

  return (
    <div className={`min-h-screen ${bgColor} flex flex-col justify-center py-12 sm:px-6 lg:px-8 transition-colors duration-200`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className={`mt-6 text-center text-3xl font-extrabold ${textColor}`}>
          Create your account
        </h2>
        <p className={`mt-2 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Step {step} of 3
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`${cardBgColor} py-8 px-4 shadow sm:rounded-lg sm:px-10 transition-colors duration-200`}>
          {message.text && (
            <div className={`mb-4 p-3 rounded-md ${message.type === 'success' 
              ? (isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800')
              : (isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800')
            }`}>
              {message.text}
            </div>
          )}

          {/* Step 1: Email Input */}
          {step === 1 && (
            <form className="space-y-6" onSubmit={handleSendEmailOtp}>
              <div>
                <label htmlFor="email" className={`block text-sm font-medium ${labelColor}`}>
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.email 
                        ? (isDarkMode ? 'border-red-500' : 'border-red-300') 
                        : inputBorderColor
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${inputFocusRing} sm:text-sm ${inputBgColor} ${textColor}`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${buttonBg} focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-blue-500' : 'focus:ring-indigo-500'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? 'Sending...' : 'Send Verification OTP'}
                </button>
              </div>

              <div className="text-center">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already have an account?{' '}
                  <Link to="/signin" className={`font-medium ${linkColor}`}>
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          )}

          {/* Step 2: Email OTP Verification */}
          {step === 2 && (
            <form className="space-y-6" onSubmit={handleVerifyEmailOtp}>
              <div>
                <label htmlFor="otp" className={`block text-sm font-medium ${labelColor}`}>
                  Enter OTP sent to <span className="font-semibold">{formData.email}</span>
                </label>
                <div className="mt-1">
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setOtp(value);
                      if (errors.otp) setErrors({ ...errors, otp: '' });
                    }}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.otp 
                        ? (isDarkMode ? 'border-red-500' : 'border-red-300') 
                        : inputBorderColor
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${inputFocusRing} sm:text-sm ${inputBgColor} ${textColor}`}
                  />
                </div>
                {errors.otp && (
                  <p className="mt-1 text-sm text-red-500">{errors.otp}</p>
                )}
                <div className="mt-2 text-sm text-right">
                  <button
                    type="button"
                    onClick={handleResendEmailOtp}
                    disabled={resendCooldown > 0 || loading}
                    className={`${linkColor} disabled:${isDarkMode ? 'text-gray-600' : 'text-gray-400'} disabled:cursor-not-allowed`}
                  >
                    {resendCooldown > 0 
                      ? `Resend OTP in ${resendCooldown}s` 
                      : 'Resend OTP'}
                  </button>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className={`w-1/2 flex justify-center py-2 px-4 border ${secondaryButtonBorder} rounded-md shadow-sm text-sm font-medium ${secondaryButtonBg} focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-blue-500' : 'focus:ring-indigo-500'
                  }`}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || otp.length !== 4}
                  className={`w-1/2 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${buttonBg} focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-blue-500' : 'focus:ring-indigo-500'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>

              <div className="text-center">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already have an account?{' '}
                  <Link to="/signin" className={`font-medium ${linkColor}`}>
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          )}

          {/* Step 3: Account Creation (now includes phone input) */}
          {step === 3 && (
            <form className="space-y-6" onSubmit={handleCreateAccount}>
              <div>
                <label htmlFor="name" className={`block text-sm font-medium ${labelColor}`}>
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.name 
                        ? (isDarkMode ? 'border-red-500' : 'border-red-300') 
                        : inputBorderColor
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${inputFocusRing} sm:text-sm ${inputBgColor} ${textColor}`}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className={`block text-sm font-medium ${labelColor}`}>
                  Phone Number
                </label>
                <div className="mt-1">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.phone 
                        ? (isDarkMode ? 'border-red-500' : 'border-red-300') 
                        : inputBorderColor
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${inputFocusRing} sm:text-sm ${inputBgColor} ${textColor}`}
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className={`block text-sm font-medium ${labelColor}`}>
                  Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                      errors.password 
                        ? (isDarkMode ? 'border-red-500' : 'border-red-300') 
                        : inputBorderColor
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${inputFocusRing} sm:text-sm ${inputBgColor} ${textColor}`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    ) : (
                      <EyeIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className={`block text-sm font-medium ${labelColor}`}>
                  Confirm Password
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                      errors.confirmPassword 
                        ? (isDarkMode ? 'border-red-500' : 'border-red-300') 
                        : inputBorderColor
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none ${inputFocusRing} sm:text-sm ${inputBgColor} ${textColor}`}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    ) : (
                      <EyeIcon className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className={`w-1/2 flex justify-center py-2 px-4 border ${secondaryButtonBorder} rounded-md shadow-sm text-sm font-medium ${secondaryButtonBg} focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-blue-500' : 'focus:ring-indigo-500'
                  }`}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-1/2 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${buttonBg} focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDarkMode ? 'focus:ring-blue-500' : 'focus:ring-indigo-500'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>

              <div className="text-center">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already have an account?{' '}
                  <Link to="/signin" className={`font-medium ${linkColor}`}>
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;



